import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, finalize, startWith, takeWhile, timeoutWith } from 'rxjs/operators';

import { logger } from '@assess/shared/logger';

/**
 * A service that tracks the initial rendering of a dynamic component hierarchy. Since a dynamic components are
 * ephemeral, the service should be considered component scoped.
 *
 * The RenderTrackingService should be injected at most once at the root of where dynamic component rendering originates
 * so that multiple instances aren't registered and tracking is fragmented.
 */
@Injectable()
export class RenderTrackingService implements OnDestroy {
  private readonly debounceTime = 250;
  private readonly timeout = 10_000;
  private readonly index = new Map<string, boolean>();

  private rendering!: BehaviorSubject<boolean> | undefined;
  private _rendering$!: Observable<boolean>;

  get rendering$() {
    return this._rendering$;
  }

  constructor() {
    this.reset();
  }

  ngOnDestroy() {
    this.index.clear();

    this.rendering?.next(false);
    this.rendering?.complete();
  }

  setRendering(uuid: string, rendering: boolean) {
    this.index.set(uuid, rendering);

    this.next();
  }

  unregister(uuid: string) {
    this.index.delete(uuid);

    this.next();
  }

  reset() {
    this.index.clear();

    this.rendering?.complete();
    this.rendering = new BehaviorSubject<boolean>(true);

    this._rendering$ = this.rendering.asObservable().pipe(
      distinctUntilChanged(),
      debounceTime(this.debounceTime),
      takeWhile((rendering) => rendering, true),
      startWith(true),
      timeoutWith(this.timeout, of(false)),
      finalize(() => {
        if (this.indexHasRendering()) {
          logger.error(`Render tracking exceeded timeout of ${this.timeout}`);
        }
      })
    );
  }

  private next() {
    this.rendering?.next(this.indexHasRendering());
  }

  private indexHasRendering() {
    return Array.from(this.index.values()).some(Boolean);
  }
}
